/**
 * Command Center feed (Oct 2026): a read-only daily pull of designboom KPIs by Timo's Command Center (Firebase project
 * timosplayground-6f821). Pure logic lives here so it is unit-tested (shared/test/commandCenterFeed.test.mjs) and so
 * every number comes from the same helpers the hub pages use. The HTTP endpoint is `commandCenterFeed`
 * (functions/src/callables/commandCenterFeed.ts); settings (on/off, keys, allowed service account, metric switches) are
 * managed on Admin → Command Center feed. Metric list + sources: docs/COMMAND-CENTER-FEED.md.
 *
 * DATA GOVERNANCE (decided 23 Oct 2026, Michael): no pay data leaves the hub. Commission metrics are performance INPUTS
 * only (collected, open, target); no tier rates, earned commission, accruals or paid amounts. Command Center applies
 * its own tier table. Rates are never sent either: achievement = collected ÷ target is computed on the other side.
 */
import { commissionCycleEndDate } from './commissions.js';
import { paypalCategoryOf, paypalDateOf, paypalIsRevenue, paypalRecUsd, PAYPAL_REVENUE_CATEGORIES, } from './paypalRevenue.js';
import { USL_DESIGNBOOM, uslIsBdSalesLine } from './usl.js';
export const CC_FEED_MAX_DAYS = 400;
export const CC_FEED_SETTINGS_DOC = 'commandCenterFeed';
/** The only people the feed ever returns (Timo's spec). Anything outside this list is never served. */
export const CC_FEED_USL_REPS = ['anja.raeder@daaily.com', 'tullia.vitturi-lochra@daaily.com'];
export const CC_FEED_COMMISSION_PEOPLE = [
    'anja.raeder@daaily.com', 'tullia.vitturi-lochra@daaily.com', 'timo.behrend@daaily.com', 'tim.spears@daaily.com', 'sofia.lekka@daaily.com',
];
export const CC_FEED_AREAS = {
    usl: 'USL billed revenue',
    openPayments: 'Open receivables',
    paypal: 'designboom PayPal revenue',
    commission: 'Commission inputs',
    directory: 'Outreach directory',
};
export const CC_FEED_METRICS = [
    { id: 'usl.designboom.billed', area: 'usl', label: 'designboom billed revenue (Salesforce payments), per day' },
    { id: 'usl.designboom.programmatic', area: 'usl', label: 'designboom Programmatic revenue (monthly stream), per month' },
    { id: 'usl.rep.billed', area: 'usl', label: 'Rep New Sales billed, per day, per rep' },
    { id: 'usl.openPayments', area: 'openPayments', label: 'Open receivables per rep, today' },
    { id: 'usl.openPayments.overdue', area: 'openPayments', label: 'Overdue receivables per rep, today' },
    { id: 'paypal.designboom.gross', area: 'paypal', label: 'designboom PayPal revenue (USD), per day' },
    { id: 'paypal.designboom.count', area: 'paypal', label: 'designboom PayPal payments, per day' },
    { id: 'paypal.designboom.gross.byCategory', area: 'paypal', label: 'designboom PayPal revenue (USD) by category, per day' },
    { id: 'commission.collected', area: 'commission', label: 'Commissionable collected revenue, year to date, per assessment' },
    { id: 'commission.open', area: 'commission', label: 'Commissionable open (planned) revenue, today' },
    // Served by the separate `outreachDirectory` endpoint (same switch, accounts and keys): the people enabled for
    // Outreach in the hub, for Timo's stand-alone Outreach (Oct 2026). Name, email, team, title, Outreach signature.
    { id: 'outreach.directory', area: 'directory', label: 'Outreach directory: people enabled for Outreach (name, email, team, title, signature)' },
];
export const OUTREACH_DIRECTORY_METRIC = 'outreach.directory';
export const CC_FEED_METRIC_IDS = CC_FEED_METRICS.map((m) => m.id);
export const CC_FEED_DEFAULT_SETTINGS = { enabled: false, allowedServiceAccounts: [], keys: [], disabledMetrics: [] };
export const CC_FEED_MAX_KEYS = 2;
/** Google service-account address: a custom account (…@<project>.iam.gserviceaccount.com) or a default runtime account
 *  (Cloud Functions v2 / Cloud Run: <number>-compute@developer.gserviceaccount.com; v1: <project>@appspot.gserviceaccount.com). */
export function ccIsServiceAccountEmail(email) {
    return /^[a-z0-9][a-z0-9._-]*@([a-z0-9-]+\.iam|developer|appspot)\.gserviceaccount\.com$/.test(email.trim().toLowerCase());
}
/** Request gate, in order. `keyHash` is the SHA-256 of the presented key; `oidcEmail` the verified token's email
 *  (null when missing/invalid). Returns the matching key id, or the HTTP status + reason. */
export function ccFeedAuthorize(s, req) {
    if (!s.enabled)
        return { ok: false, status: 503, reason: 'Feed is switched off.' };
    if (!req.oidcEmail)
        return { ok: false, status: 401, reason: 'Missing or invalid Google ID token.' };
    if (!s.allowedServiceAccounts.map((e) => e.toLowerCase()).includes(req.oidcEmail.toLowerCase())) {
        return { ok: false, status: 403, reason: 'Caller is not an allowed service account.' };
    }
    const key = req.keyHash ? s.keys.find((k) => k.hash === req.keyHash) : undefined;
    if (!key)
        return { ok: false, status: 401, reason: 'Missing or invalid feed key.' };
    return { ok: true, keyId: key.id };
}
// ── Query ──────────────────────────────────────────────────────────────────────────────────────────────────────────
const ISO = /^\d{4}-\d{2}-\d{2}$/;
const isRealDate = (d) => ISO.test(d) && new Date(`${d}T00:00:00Z`).toISOString().slice(0, 10) === d;
export function ccFeedParseQuery(q, today) {
    const metrics = typeof q.metrics === 'string' ? [...new Set(q.metrics.split(',').map((m) => m.trim()).filter(Boolean))] : [];
    if (metrics.length === 0)
        return { ok: false, error: 'metrics is required (comma-separated ids).' };
    const unknown = metrics.filter((m) => !CC_FEED_METRIC_IDS.includes(m));
    if (unknown.length)
        return { ok: false, error: `Unknown metric id(s): ${unknown.join(', ')}.` };
    const from = typeof q.from === 'string' ? q.from : '';
    const to = typeof q.to === 'string' ? q.to : '';
    if (!isRealDate(from) || !isRealDate(to))
        return { ok: false, error: 'from and to must be dates YYYY-MM-DD.' };
    if (from > to)
        return { ok: false, error: 'from is after to.' };
    if (ccDaysBetween(from, to) + 1 > CC_FEED_MAX_DAYS)
        return { ok: false, error: `The range is at most ${CC_FEED_MAX_DAYS} days.` };
    // Nothing after today is served (no forecasts in this feed).
    return { ok: true, metrics, from, to: to > today ? today : to };
}
// ── Date helpers ───────────────────────────────────────────────────────────────────────────────────────────────────
export function ccDaysBetween(a, b) {
    return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}
export function ccEachDay(from, to) {
    const out = [];
    for (let t = Date.parse(`${from}T00:00:00Z`), end = Date.parse(`${to}T00:00:00Z`); t <= end; t += 86_400_000) {
        out.push(new Date(t).toISOString().slice(0, 10));
    }
    return out;
}
export function ccYearsInRange(from, to) {
    const out = [];
    for (let y = Number(from.slice(0, 4)); y <= Number(to.slice(0, 4)); y++)
        out.push(y);
    return out;
}
/** A USL issue date ("DD.MM.YYYY", sometimes already ISO) → ISO, or null. */
export function ccIsoFromUslDate(s) {
    const m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})/.exec((s ?? '').trim());
    if (m)
        return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
    return ISO.test((s ?? '').slice(0, 10)) ? s.slice(0, 10) : null;
}
/** Date in Europe/Zurich (the business calendar) for an ISO timestamp or Date. */
export function ccZurichDate(t) {
    return new Date(t).toLocaleDateString('en-CA', { timeZone: 'Europe/Zurich' });
}
const r2 = (n) => Math.round(n * 100) / 100;
/** One point per day in [from, to], zero-filled. A day is complete once the source's last refresh happened on a
 *  LATER day (`refreshedOn`, Zurich date); the refresh day itself and anything after stay incomplete. */
export function ccDailyPoints(from, to, byDate, refreshedOn) {
    return ccEachDay(from, to).map((date) => ({ date, value: r2(byDate.get(date) ?? 0), complete: !!refreshedOn && date < refreshedOn }));
}
/** Monthly targets (12, Jan..Dec) of the years in range → target rows, plus the year total. */
export function ccTargetsFromMonthly(byYear, from, to) {
    const out = [];
    for (const [y, months] of [...byYear.entries()].sort((a, b) => a[0] - b[0])) {
        out.push({ period: 'year', start: `${y}-01-01`, value: r2(months.reduce((s, n) => s + (n || 0), 0)) });
        months.forEach((v, i) => {
            const start = `${y}-${String(i + 1).padStart(2, '0')}-01`;
            if (start.slice(0, 7) >= from.slice(0, 7) && start.slice(0, 7) <= to.slice(0, 7))
                out.push({ period: 'month', start, value: r2(v) });
        });
    }
    return out;
}
// ── USL ────────────────────────────────────────────────────────────────────────────────────────────────────────────
/** Daily CHF from stored USL lines (issue date), filtered. Sums equal the page's monthly cube cells. */
export function ccUslDaily(lines, keep) {
    const out = new Map();
    for (const l of lines) {
        if (!keep(l))
            continue;
        const d = ccIsoFromUslDate(l.issueDate);
        if (!d)
            continue;
        out.set(d, (out.get(d) ?? 0) + (l.chf || 0));
    }
    return out;
}
export const ccIsDesignboomPaymentLine = (l) => l.brand === USL_DESIGNBOOM;
export const ccIsRepSalesLine = (bd) => (l) => l.bd === bd && uslIsBdSalesLine(l);
// ── Open payments ──────────────────────────────────────────────────────────────────────────────────────────────────
/** Overdue = the report's own "Overdue Status" says anything other than current ("01 - Current"). */
export function ccIsOverdue(p) {
    const s = (p.overdueStatus ?? '').trim();
    return !!s && !/^0?1\b/.test(s) && !/current/i.test(s);
}
export function ccOpenTotals(rows, bd) {
    let open = 0;
    let overdue = 0;
    for (const r of rows) {
        if (r.bd !== bd)
            continue;
        open += r.chf || 0;
        if (ccIsOverdue(r))
            overdue += r.chf || 0;
    }
    return { open: r2(open), overdue: r2(overdue) };
}
/** A brand's open and overdue CHF across all reps, from each row's cost-unit brand split. `null` when the snapshot
 *  predates the split (no row carries `byBrand`). */
export function ccOpenBrandTotals(rows, brand) {
    if (!rows.some((r) => r.byBrand))
        return null;
    let open = 0;
    let overdue = 0;
    for (const r of rows) {
        const v = r.byBrand?.[brand] ?? 0;
        open += v;
        if (ccIsOverdue(r))
            overdue += v;
    }
    return { open: r2(open), overdue: r2(overdue) };
}
// ── PayPal ─────────────────────────────────────────────────────────────────────────────────────────────────────────
/** Daily USD gross + count (total and by category) on the page's default 'paid' basis. */
export function ccPaypalDaily(rows, fx) {
    const gross = new Map();
    const count = new Map();
    const byCategory = new Map(PAYPAL_REVENUE_CATEGORIES.map((c) => [c, new Map()]));
    for (const r of rows) {
        if (!paypalIsRevenue(r))
            continue;
        const d = paypalDateOf(r, 'paid').slice(0, 10);
        if (!ISO.test(d))
            continue;
        const v = paypalRecUsd(fx, r);
        gross.set(d, (gross.get(d) ?? 0) + v);
        count.set(d, (count.get(d) ?? 0) + 1);
        const m = byCategory.get(paypalCategoryOf(r.item_name));
        m.set(d, (m.get(d) ?? 0) + v);
    }
    return { gross, count, byCategory };
}
/** End-of-year goals (USD) → year targets; `category` null = the total. */
export function ccPaypalTargets(goals, category) {
    return goals
        .map((g) => ({ g, v: category ? g.goals[category] ?? 0 : Object.values(g.goals).reduce((s, n) => s + (n || 0), 0) }))
        .filter((x) => x.v > 0)
        .map((x) => ({ period: 'year', start: `${x.g.year}-01-01`, value: r2(x.v) }));
}
// ── Commission inputs ──────────────────────────────────────────────────────────────────────────────────────────────
/** One point per computed assessment of the person: year-to-date collected as of that assessment. Ended assessments
 *  sit on their quarter-end date and are complete once the person's statement is released; the live (not yet
 *  ended) assessment sits on its computation day and is never complete. */
export function ccCommissionCollectedPoints(reports, bdKey, from, to, today) {
    const out = [];
    for (const { report, releasedBdKeys } of reports) {
        const row = report.bdRows.find((r) => r.bdKey === bdKey);
        if (!row)
            continue;
        const end = commissionCycleEndDate(report.fiscalYear, report.cycle);
        const ended = today > end;
        const date = ended ? end : ccZurichDate(report.computedAt.toDate());
        if (date < from || date > to)
            continue;
        out.push({ date, value: r2(row.collected), complete: ended && releasedBdKeys.includes(bdKey), period: report.period });
    }
    return out.sort((a, b) => (a.date < b.date ? -1 : 1));
}
//# sourceMappingURL=commandCenterFeed.js.map